import {Color} from '@benchling/nucleus-ui';
import {
  Button,
  CloseButton,
  createStyles,
  Divider,
  Group,
  Paper,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import {useClickOutside} from '@mantine/hooks';
import React, {useCallback, useEffect, useRef, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {LLM_CHAT_ANALYTICS_ACTIONS} from 'constants/analytics-products/LLMChatConstants';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {getProgressiveDisclosureCategories, ProgressiveDisclosureCategory} from './ProgressiveDisclosureData';

interface ProgressiveDisclosureProps {
  onHoverOption: (prompt: string | null) => void;
  onSelectOption: (prompt: string) => void;
}

const useStyles = createStyles((theme) => ({
  container: {
    paddingTop: theme.spacing.md,
    paddingRight: theme.spacing.md,
    paddingBottom: theme.spacing.md,
    paddingLeft: theme.spacing.md,
  },
  optionItem: {
    display: 'block',
    width: '100%',
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    borderRadius: 0,
    fontSize: theme.fontSizes.md,
    color: Color.GRAY_900,
    textAlign: 'left',
    '&:hover': {
      backgroundColor: theme.colors.blue[0],
    },
  },
  optionsPanel: {
    borderRadius: theme.radius.lg,
    overflow: 'hidden',
    padding: theme.spacing.sm,
  },
  optionsPanelTitle: {
    color: Color.GRAY_500,
  },
  optionsPanelTitleIcon: {
    width: 14,
    height: 14,
    fontSize: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: Color.GRAY_500,
    '& svg': {
      color: Color.GRAY_500,
    },
  },
  divider: {
    borderColor: theme.colors.gray[2],
  },
  categoryPill: {
    fontWeight: theme.other.fontWeights.normal,
    backgroundColor: theme.white,
    color: Color.GRAY_900,
    border: `1px solid ${Color.GRAY_250}`,
    '&:hover': {
      backgroundColor: Color.BLUE_100,
      borderColor: Color.BLUE_100,
      color: Color.BLUE_450,
    },
    '&:hover $categoryPillLeftIcon': {
      opacity: 1,
    },
  },
  categoryPillLeftIcon: {
    marginRight: theme.spacing.sm,
    opacity: 0.5,
  },
  categoryPillIcon: {
    width: 14,
    height: 14,
    fontSize: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

export default function ProgressiveDisclosure({
  onHoverOption,
  onSelectOption,
}: ProgressiveDisclosureProps): JSX.Element {
  const {classes} = useStyles();
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);
  const lastHoveredOptionRef = useRef<string | null>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const categories = getProgressiveDisclosureCategories();
  const activeCategory = categories.find((c) => c.id === activeCategoryId) ?? null;

  const clearHoverTimer = useCallback(() => {
    if (hoverTimerRef.current != null) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  }, []);

  useEffect(() => clearHoverTimer, [clearHoverTimer]);

  const dismissPanel = useCallback(() => {
    clearHoverTimer();
    setActiveCategoryId(null);
    onHoverOption(null);
    lastHoveredOptionRef.current = null;
  }, [onHoverOption, clearHoverTimer]);

  const containerRef = useClickOutside<HTMLDivElement>(dismissPanel);

  const handleCategoryClick = useCallback((category: ProgressiveDisclosureCategory) => {
    setActiveCategoryId(category.id);
    lastHoveredOptionRef.current = null;
    trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.CLICKED_PROGRESSIVE_DISCLOSURE_CATEGORY, {
      categoryId: category.id,
    });
  }, []);

  const handleOptionClick = useCallback(
    (categoryId: string, optionId: string, prompt: string) => {
      clearHoverTimer();
      trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.SUBMITTED_PROGRESSIVE_DISCLOSURE_OPTION, {
        categoryId,
        optionId,
      });
      onHoverOption(null);
      setActiveCategoryId(null);
      onSelectOption(prompt);
    },
    [onHoverOption, onSelectOption, clearHoverTimer]
  );

  const renderCategoryButton = (category: ProgressiveDisclosureCategory): JSX.Element => (
    <Button
      key={category.id}
      variant="ghost"
      radius="xl"
      size="sm"
      classNames={{root: classes.categoryPill, leftIcon: classes.categoryPillLeftIcon}}
      leftIcon={
        <BenchlingIcon icon={category.icon} aria-hidden={true} className={classes.categoryPillIcon} />
      }
      onClick={() => {
        handleCategoryClick(category);
      }}
    >
      {category.label}
    </Button>
  );

  return (
    <div ref={activeCategoryId != null ? containerRef : undefined} className={classes.container}>
      {activeCategory == null && (
        <Group spacing="sm" position="center">
          {categories.map((category) => renderCategoryButton(category))}
        </Group>
      )}
      {activeCategory != null && (
        <Paper withBorder={true} className={classes.optionsPanel}>
          <Group position="apart" px="md" pt="sm" pb={4}>
            <Group spacing="sm">
              <BenchlingIcon
                icon={activeCategory.icon}
                aria-hidden={true}
                className={classes.optionsPanelTitleIcon}
              />
              <Text size="sm" className={classes.optionsPanelTitle}>
                {activeCategory.label}
              </Text>
            </Group>
            <CloseButton size="sm" onClick={dismissPanel} aria-label="Close suggestions" />
          </Group>
          <Stack spacing={0} pb={0}>
            {activeCategory.options.map((option, index) => (
              <React.Fragment key={option.id}>
                {index > 0 && <Divider className={classes.divider} />}
                <UnstyledButton
                  className={classes.optionItem}
                  onMouseEnter={() => {
                    const key = `${activeCategory.id}:${option.id}`;
                    hoverTimerRef.current = setTimeout(() => {
                      if (lastHoveredOptionRef.current !== key) {
                        lastHoveredOptionRef.current = key;
                        trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.HOVERED_PROGRESSIVE_DISCLOSURE_OPTION, {
                          categoryId: activeCategory.id,
                          optionId: option.id,
                        });
                      }
                    }, 400);
                    onHoverOption(option.prompt);
                  }}
                  onMouseLeave={() => {
                    clearHoverTimer();
                    onHoverOption(null);
                  }}
                  onClick={() => {
                    handleOptionClick(activeCategory.id, option.id, option.prompt);
                  }}
                >
                  {option.label}
                </UnstyledButton>
              </React.Fragment>
            ))}
          </Stack>
        </Paper>
      )}
    </div>
  );
}
